/**
 * Admin → Reseller dashboard.
 *
 * Backed by GET /v1/admin/dashboard. Same gate as the rest of /v1/admin/*:
 * the middleware returns 403 to anyone whose JWT claims tenant_type isn't
 * platform/reseller, so the sidebar link is hidden for non-resellers and
 * the route guard blocks direct URL access.
 */

import { useQuery } from '@tanstack/react-query';

import { api } from '@/lib/api';

export interface ResellerCustomerCounts {
  active: number;
  suspended: number;
  closed: number;
}

export interface ResellerStats {
  messages_sent: number;
  messages_delivered: number;
  /** Sum of message.cost across all child tenants (what customers paid). */
  customer_spend: number;
  /** Sum of commission_in transactions on the reseller's own balance. */
  reseller_margin: number;
}

export interface ResellerTopCustomer {
  id: number;
  uuid: string;
  name: string;
  country?: string;
  sent: number;
  spend: number;
}

/** Same shape as ResellerTopCustomer but ranked by wholesale spend
 *  (what the reseller paid the platform per customer). Powers the
 *  "Top customers by wholesale spend" card on /billing. */
export interface ResellerTopCustomerWholesale {
  id: number;
  uuid: string;
  name: string;
  country?: string;
  sent: number;
  wholesale_spend: number;
}

export interface ResellerLowBalanceCustomer {
  id: number;
  uuid: string;
  name: string;
  balance: number;
  threshold: number;
}

export interface ResellerRecentAuditEntry {
  id: number;
  actor_email: string;
  method: string;
  path: string;
  status_code: number;
  /** RFC3339 UTC. */
  created_at: string;
}

export interface ResellerMarginPrefix {
  /** Dial prefix (e.g. "1", "52", "1786") resolved via longest-prefix-match
   *  against the reseller's rates. Rates carry no country dimension, so
   *  the UI renders it verbatim as "+<prefix>". */
  prefix: string;
  messages: number;
  delivered: number;
  revenue: number;
  cost: number;
  margin: number;
}

export interface ResellerDashboard {
  /** Range echoed back; reflects whatever the picker asked for (clamped). */
  from: string;
  to: string;
  totals: ResellerCustomerCounts;
  stats: ResellerStats;
  top_customers: ResellerTopCustomer[];
  top_customers_wholesale: ResellerTopCustomerWholesale[];
  low_balance: ResellerLowBalanceCustomer[];
  recent_audit: ResellerRecentAuditEntry[];
  margin_by_prefix: ResellerMarginPrefix[];
}

export interface AdminDashboardRange {
  /** RFC3339 lower bound. */
  from: string;
  /** RFC3339 upper bound. */
  to: string;
}

const DASHBOARD_KEY = ['admin', 'dashboard'] as const;

/**
 * One-shot fetch for /admin/dashboard. The API bundles header counts,
 * range KPIs, top customers by volume, and low-balance alerts so the
 * page paints in a single round-trip instead of waterfall.
 *
 * range is optional; the API defaults to the last 30 days when omitted.
 * Keys include from/to so flipping the picker invalidates the cache.
 */
export function useAdminDashboard(opts?: { enabled?: boolean; range?: AdminDashboardRange }) {
  const { range } = opts ?? {};
  return useQuery({
    queryKey: [...DASHBOARD_KEY, range?.from ?? null, range?.to ?? null],
    queryFn: () => {
      const qs = range
        ? `?from=${encodeURIComponent(range.from)}&to=${encodeURIComponent(range.to)}`
        : '';
      return api.get<ResellerDashboard>(`/v1/admin/dashboard${qs}`);
    },
    staleTime: 30_000,
    enabled: opts?.enabled ?? true,
  });
}
